import { NFSE_PATTERN } from "../shared/lists/nfse-patterns";
import { COMPANY_TAB } from "../shared/lists/company-tabs";
import { Address } from "./address";
import { BOOLEAN_TWO } from "../shared/common-enums/boolean-two";
import { BOOLEAN_ONE } from "../shared/common-enums/boolean-one";
import { RegEspTrib } from "@app/shared/types/fiscal/reg-esp-trib";
import { APPORTIONMENT_CODE } from "@app/shared/enums/apportionment";
import { ObjectId } from "t5-common";
import { USE_DIAGWEB } from "@app/shared/lists/diagweb/diagweb-status";

export enum COMPANY_STATE {
  test,
  active,
  blocked,
  integrated,
  MEKANICGO_WEB,
  MAKENA_ADMIN,
  MAKENA_CLIENT,
  PO360,
  CONTA_AZUL = 11,
  MEKANICGO_NAUTICAL = 12,
}

export enum PROFIT_CALC_TYPE{
  /** @field cost from @interface Part */
  LAST_COST = 1,
  /** @field averageCost from @interface Part */
  AVERAGE_COST
}

/**
 * @external
 * Keep WabAdmin updated with these same values
 */
export enum FRAGA_CATALOG {
  "BLOCKED" = 1,
  "PAID" = 2,
  "FREE" = 3
}

export enum SEND_CODE {
  "BLOCK_SEND_CODE" = 1,
  "ALLOW_SEND_CODE" = 2
}

export enum TYPE_VEHICLE {
  "CAR" = 1,
  "TRUCK" = 2,
  "AGRO" = 3,
  "MOTORCYCLE" = 4,
  "BUS" = 5,
  "BOAT" = 6,
}

export enum IA_MODULE_STATE {
    INACTIVE = 1,
    ACTIVE = 2
}

export interface IDigitalCertificateConsult {
  lastConsult: Date;
  dueDate: Date;
}

export interface Company {
  id?: string;
  socialName?: string;
  fancyName?: string;
  cnpj?: string; // Cadastro Nacional da Pessoa Jurídica

  /**
   * Código de Regime Tributário
   *
   * "1" - Simples Nacional
   *
   * "2" - Simples Nacional – excesso de sublimite de receita bruta
   *
   * "3" - Regime Normal
   *
   * "4" - Simples Nacional - MEI
   */
  crt?: '1' | '2' | '3' | '4';
  currentState?: COMPANY_STATE;
  owner?: string;
  address?: Address;
  createdAt?: string;
  stateRegistration?: string;
  phone?: string;
  phone2?: string;
  email?: string;
  /** Checked by default, consider null or undefined as true */
  sendCode?: SEND_CODE;
  companyLogo?: string;
  /** Checked by default, consider null or undefined as true */
  complementaryOS?: BOOLEAN_TWO;
  discountControl?: BOOLEAN_TWO;
  associateLogo?: string;
  favorite?: number;
  osCount?: BOOLEAN_TWO;
  sms?: number;
  fcmTopic?: string;
  fcmTopics?: [];
  lang?: number;
  typeVehicle?: TYPE_VEHICLE;
  costAlert?: BOOLEAN_TWO;
  infoCarQuery?: number;
  // absent in the API
  infoCarContracted?: number;
  companyLogoBlob?: Blob;
  companyLogoInput?: string;
  associationLogoBlob?: Blob;
  associationLogoInput?: string;
  site?: string;
  tokenWhats?: string;
  descriptionForUnifyServices: string;
  useUnifiedServices: boolean;
  sendPisCofinsHighlightValue: boolean;

  /**
   * if true, try integrate with migrate after create company
   **/
  migrateIntegrated?: boolean;

  /** If true, forbid negative stock **/
  controlStock?: boolean;


  /**
   * Código de tributação do município (Codígo LC)
   */
  tributMunicipio?: string;
  iteListServico?: string;
  numberInvoice?: string;
  numberInvoiceNFC?: string;
  numberInvoiceNFS?: string;
  numberGerencial?: string;

  serieInvoice?: number;
  serieInvoiceNFC?: number;
  serieInvoiceNFS?: number;
  serieGerencial?: number;

  authorized?: Array<string>;

  /** used for migrate integration */
  accessKeyInvoice?: string;

  /** used for migrate integration */
  lastNSU?: string;

  /** Natureza da operação
   *
   * an code of a item from `NATUREZAS_DA_OPERACAO`
   **/
  natOp?: string;

  /** Operação de tributação
   *
   * A – Sem dedução
   * B – Com dedução / materiais
   * C - Imune / Isenta de ISSQN
   * D - Devolução
   * J - Intermediação
   **/
  descDSFNET?: 'A' | 'B' | 'C' | 'D' | 'J';

  regEspTrib?: RegEspTrib;

  /** Código de Verificação de RPS
   *
   * Alguns padrões precisam liberar uma faixa de RPS no site da prefeitura antes de emitir.
   * Além do número do RPS, é necessário informar o código de verificação que é fornecido nesta lista.
   */
  verificaRPS?: string;

  /**
   * Padrão de nota fiscal utilizado pela prefeitura município da empresa para emissão de NFSe
   */
  patternNFSE?: NFSE_PATTERN;

  /** Inscrição Municipal */
  IM?: string;

  /**
   * Exigibilidade do ISS em notas de Serviços (NFSe)
   * natOp e tpTributacao em notas de Peças (NFe/NFCe)
   *
   * usar codigos da const NATUREZAS_DE_OPERAÇÃO
   */
  exigibilidade?: number;

  /**
   * Exigibilidade do ISS em notas de Serviços (NFSe)
   * natOp e tpTributacao em notas de Peças (NFe/NFCe)
   *
   * usar codigos da const NATUREZAS_DE_OPERAÇÃO
   * 
   */
  exigibilidadeRetIss?: number;

  /** Município de incidência de ISS */
  munIncidISS?: "Não informa" | "Cliente" | "Prestador";

  /** Alíquota de ISS */
  ISS?: number;

  /**
   *  Valor mínimo para retenção de ISS
   *
   * @TODO não está na tela, mas está no banco
   */
  minISS?: number;


  /** Codigo CNAE - Classificação Nacional de Atividades Econômicas */
  cnaeCode?: string;

  /** The cashier value in money since last cashier opening, use CashierService to retrieve updated data */
  cashierValue?: number;

  /** @ignore Used into Makeasy system */
  brand?: string;
  /** @ignore Used into Makeasy system */
  tab?: COMPANY_TAB[];

  /** Checked by default, consider null or undefined as true */
  openOS?: BOOLEAN_ONE;

  /**
   * @optional
   * An masked CNPJ, used to work with multiple filials
   *
   * For some requests, the CNPJ in header is different, in order to get/save
   * the data on the matrix company instead the current logged-in company
   */
  cnpjMatrix?: string;

  /**
   * if true, the company prohibits the creation of a low with a date earlier than the title creation date
   */
  controlFinc?: boolean;

  /** Indicate if the company can use the fraga service to consult catalogs */
  fragaCatalog?: FRAGA_CATALOG;

  /**
   * Indicate if federal retention values should be reduced of NFSe values generated from OSs
   *
   * Attention: ISS is not a federal retention (is municipal), use `deduISS` to configure it
   */
  deduNFSE: boolean;

  /** Indicate if federal retention values should be reduced of NFe/NFCe values generated from OSs */
  deduNFE: boolean;

  /** Indicate if ISS retention should be deduced of NFSe values */
  deduISS: boolean;

  /** Indicate if the company can use the boleto service */
  boletos?: number;

    /** Indicate if the company can use the importador XML service */
  importador?: number;

  newMenu?: BOOLEAN_TWO;
  mecLabors?: boolean;
  mecParts?: boolean;
  useAvarias?: boolean;
  checklistSim?: boolean;
  profitCalcType?: PROFIT_CALC_TYPE;

  // Indicate if the company can use the fraga service to consult vehicles
  readonly fragaPlate?: BOOLEAN_ONE;

  /** Deadline in days for the devolution to be made before a refound note.  */
  cancelDate?: number;

  /** 
   * Default inbound operation to be used at refunding a outbound devolution 
   * 
   * - (entry) ← originInvoice
   * - (out) → devolutionInvoice
   * - (entry) ← refundInvoice
   */
  operationEstorno?: ObjectId;
  
  /** 
   * Default outbound operation to be used at refunding a inbound devolution 
   * 
   * - (out) → originInvoice
   * - (entry) ← devolutionInvoice
   * - (out) → refundInvoice
   */
  operationEstornoOut?: ObjectId;

  sendDiscountNFSe: boolean;

  iaModule: IA_MODULE_STATE;

  /** Indicate if the company should use the tax reform at creating invoices (LC 214/2025) */
  useTaxReform?: boolean;

  /** Indicate if the company can use the label generator feature */
  labelGenerator?: boolean;
  saturdayIsWorkingDay: boolean;
  useDiagweb?: USE_DIAGWEB;
  useDiagwebMaintenanceModule?: boolean;
  diagwebMaintenanceConsultsLimitByMonth?: number;
  existInIntegrateMkgoDatabase?: BOOLEAN_TWO;

  customOsPdf?: boolean;

  /** indicates whether the user can apply titles directly through the service order */
  applyLowByRo: boolean;

    /** Indicate how the remainder cents of divisions will be apportioned */
  apportionment?: APPORTIONMENT_CODE;

  digitalCertificateConsult?: IDigitalCertificateConsult;

  users: string[];

  applyLowByOrder?: boolean;
}
